import classNames from 'classnames';
import * as _ from 'lodash';
import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import type { InjectedRouter } from 'react-router';
import type { Column, RowRenderProps, SortingRule } from 'react-table';
import { createStructuredSelector } from 'reselect';
import type { Role } from '../../../../../shared/model-typings';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { window } from '../../../../globals';
import { roleLabel } from '../../../../helpers/role';
import type { RolesStore } from '../../../../redux/roles';
import RolesModules from '../../../../redux/roles';
import type { ReduxStore } from '../../../../redux/store';
import TeamsModule from '../../../../redux/teams';
import type { UsersStore } from '../../../../redux/users';
import UsersModule from '../../../../redux/users';
import type { Team } from '../../../../types';
import { splitArray } from '../../../../utils';
import Loader from '../../../generic/Loader';
import PopperMenu from '../../../generic/PopperMenu';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import Tooltip from '../../../generic/Tooltip';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { NONE_ROLE } from './constants';

const ACCOUNT_DISABLED_TOOLTIP = 'This user account is disabled.';

const filterPassthrough = _.constant(true);

const disabledUserClassname = ({ enabled }: UserRow) => enabled ? undefined : 'disabled-user';

type UserEditDropdownButtonProps = {
  userId: number;
  router: InjectedRouter;
  canEditUsers: boolean;
  canDeleteUser: boolean;
  confirmDeleteUser(): void;
};

const UserEditDropdownButton = (props: UserEditDropdownButtonProps) =>
  props.canEditUsers
    ? (
      <PopperMenu>
        <PopperMenu.Item
          onClick={() => props.router.push(`/account/settings/organization/users/${(props.userId)}`)}
        >
          View/Edit user
        </PopperMenu.Item>
        {props.canDeleteUser && (
          <PopperMenu.Item
            onClick={() => props.confirmDeleteUser()}
            isWarning
          >
            Remove user
          </PopperMenu.Item>
        )}
      </PopperMenu>
      )
    : null;

type ColumnRenderParameter = { original: UserRow } & Omit<RowRenderProps, 'original'>;

type UserRow = {
  id: number;
  name: string;
  email: string;
  enabled: boolean;
  roleName: string;
  teams: Array<{ name: string; roleName: string }>;
};

type ReduxStateProps = {
  status: UsersStore['status'];
  teams: Team[];
  roles: RolesStore['entities'];
};

type DispatchProps = {
  deleteUser(userId: number): void;
};

type UsersTableProps = ReduxStateProps & DispatchProps & {
  users: UsersStore['entities'];
  searchQuery: string;
  orgRoleFilter: Role | 'None' | null;
  router: InjectedRouter;
};

const UsersTable = (props: UsersTableProps) => {
  const { onConfirm } = useDialogContext();
  const {
    users,
    status,
    teams,
    roles,
    searchQuery,
    orgRoleFilter,
    router,
    deleteUser,
  } = props;

  const [sortMode, setSortMode] = useState<SortingRule>({ id: 'name', desc: false });

  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  const allUserRows = useMemo<UserRow[]>(
    () => Object.values(users)
      .map((user): UserRow => ({
        id: user.id,
        name: user.full_name || user.username || '(None)',
        email: user.email || '(None)',
        enabled: user.enabled,
        roleName: roleLabel(roles, user.userRole?.roleId || null),
        teams: teams.reduce((acc, team) => {
          const teamUser = team.teamUsers?.find(({ userId }) => userId === user.id);
          if (teamUser) {
            return [...acc, {
              name: team.name,
              roleName: roleLabel(roles, teamUser.roleId),
            }];
          }
          return acc;
        }, [] as Array<{ name: string; roleName: string }>),
      })),
    [users, teams, roles],
  );

  const canDeleteUser = window.hasPermission({ action: Action.Remove, resourceType: ResourceType.UserAny });

  const canEditUsers = canDeleteUser ||
    window.hasPermission({ action: Action.Manage, resourceType: ResourceType.Role });

  const confirmDeleteUser = (userId: number) => {
    if (canDeleteUser) {
      onConfirm({
        title: 'Remove User?',
        body: 'Are you sure you want to permanently remove this user? This action cannot be undone.',
        onConfirm: () => {
          setDeletingUserId(userId);
          deleteUser(userId);
        },
        confirmBtnType: 'error',
        confirmText: 'Remove',
      });
    }
  };

  const filteredUsers = (): UserRow[] => {
    //  Early return if there is no search/role filter.
    if (!props.searchQuery && !orgRoleFilter) {
      return allUserRows;
    }

    //  Filter based on search query.
    const searchLower = searchQuery.toLowerCase();
    const searchFilter = !searchQuery ? filterPassthrough : (user: UserRow) =>
      user.name.toLowerCase().includes(searchLower) || user.email.toLowerCase().includes(searchLower);
    //  Filter based on role filter.
    const roleFilter = !orgRoleFilter
      ? filterPassthrough
      : (user: UserRow) => (
          orgRoleFilter === NONE_ROLE ? user.roleName === NONE_ROLE : user.roleName === orgRoleFilter.name
        );
    //  Combine all filters, so each userRow can be tested in a single Array.filter pass.
    const allFilters = (user: UserRow) => [searchFilter, roleFilter].every(filterFn => filterFn(user));
    return allUserRows.filter(allFilters);
  };

  const getColumns = (): Array<Column<any>> => {
    const cols: Column[] = [{
      id: 'name',
      Header: (
        <TableHeader title="User" sort={{ id: 'name', currentMode: sortMode }} />
      ),
      accessor: 'name',
      width: 300,
      resizable: false,
      style: {
        textOverflow: 'ellipsis',
        display: 'block',
      },
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now
      Cell: ({ original }: ColumnRenderParameter) => (
        <Tooltip tooltip={ACCOUNT_DISABLED_TOOLTIP} showTooltip={!original.enabled}>
          <span>
            <b className={disabledUserClassname(original)}>{original.name}</b>
            <br />
            <span className={disabledUserClassname(original)}>{original.email}</span>
          </span>
        </Tooltip>
      ),
    }];

    if (window.hasPermission({ resourceType: ResourceType.Role, action: Action.Manage })) {
      cols.push({
        id: 'roleName',
        Header: (
          <TableHeader title="Organization Role" sort={{ id: 'roleName', currentMode: sortMode }} />
        ),
        accessor: 'roleName',
        width: 200,
        resizable: false,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now
        Cell: ({ original }: ColumnRenderParameter) => (
          <Tooltip tooltip={ACCOUNT_DISABLED_TOOLTIP} showTooltip={!original.enabled}>
            <span className={classNames(disabledUserClassname(original), 'inline-block')} style={{ whiteSpace: 'normal' }}>
              {original.roleName}
            </span>
          </Tooltip>
        ),
      }, {
        id: 'teams',
        Header: (
          <TableHeader title="Teams" />
        ),
        accessor: 'teams',
        resizable: false,
        sortable: false,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now
        Cell: ({ original }: ColumnRenderParameter) => {
          const teamRoles = original.teams.map(({ name, roleName }) => `${name} (${roleName})`);
          const teamsCount = original.teams.length;
          const listShownCount = 1;
          const tooltipShownCount = 50;
          const [first, tooltipItems] = splitArray(teamRoles, listShownCount);
          const tooltipMoreCount = teamsCount - listShownCount - tooltipShownCount;

          return (
            <>
              <div>
                <span className={disabledUserClassname(original)}>{first.join(', ')}</span>
                {teamsCount > listShownCount && (
                  <Tooltip tooltip={[
                    ...tooltipItems.map(team => <div key={team}>{team}</div>),
                    <div key="more-teams-count">{tooltipMoreCount > 0 && `+ ${tooltipMoreCount} More`}</div>,
                  ]}>
                    <span className={disabledUserClassname(original)}>
                      &nbsp; + {teamsCount - first.length} More
                    </span>
                  </Tooltip>
                )}
              </div>
              {status.loading && deletingUserId === original.id
                ? (
                  <Loader size={30} style={{ height: '30px', width: '30px' }} />
                  ) : (
                    <UserEditDropdownButton
                      userId={original.id}
                      router={router}
                      canEditUsers={canEditUsers}
                      canDeleteUser={canDeleteUser}
                      confirmDeleteUser={() => confirmDeleteUser(original.id)}
                  />
                  )}
            </>
          );
        },
        style: {
          whiteSpace: 'pre-line',
          overflow: 'visible',
          paddingRight: '15px',
        },
      });
    }
    return cols;
  };

  return (
    <div className="org-users">
      <SimpleReactTable
        data={filteredUsers()}
        columns={getColumns()}
        defaultSortMode={[sortMode]}
        onSortedChanged={(newSortMode: SortingRule) => setSortMode(newSortMode)}
        showPagination
        cacheId="organizationUsers"
      />
    </div>
  );
};

const mapStateToProps = createStructuredSelector<ReduxStore, ReduxStateProps>({
  status: UsersModule.selectors.getStatus,
  teams: TeamsModule.selectors.getTeamList,
  roles: RolesModules.selectors.getRoles,
});

const mapDispatchToProps: DispatchProps = {
  deleteUser: UsersModule.actions.delete,
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersTable);
